import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { App, Button, Dropdown, Form, Input, Modal, Select, Table, Tag, Typography } from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import SupplierContactCell from '../components/suppliers/SupplierContactCell';
import SupplierForm from '../components/suppliers/SupplierForm';
import { applySupplierSaveError } from '../components/suppliers/supplierFormErrors';
import {
  canDeleteSupplier,
  canManageSuppliers,
} from '../modules/suppliers/supplierAccessPolicy';
import { matchesSupplierSearch } from '../modules/reference-data/supplierSearch';
import type { Supplier, SupplierSubmitValues } from '../modules/suppliers/types';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import './Suppliers.css';

const { Text } = Typography;

type SupplierSortOrder = 'nameAsc' | 'nameDesc';

const EMPTY_VALUE = '—';

const Suppliers: React.FC = () => {
  const { t } = useTranslation(['suppliers', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // Search and sort states
  const supplierSearchParam = searchParams.get('search') || '';
  const [searchText, setSearchText] = useState(supplierSearchParam);
  const [sortOrder, setSortOrder] = useState<SupplierSortOrder>('nameAsc');

  const currentUser = authService.getCurrentUser();
  const canEditSuppliers = canManageSuppliers(currentUser);
  const canDeleteSuppliers = canDeleteSupplier(currentUser);

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchText(supplierSearchParam);
  }, [supplierSearchParam]);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/suppliers');
      setSuppliers(response.data);
    } catch (error) {
      feedback.error(t('suppliers:messages.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values: SupplierSubmitValues) => {
    setSubmitting(true);
    form.setFields([
      { name: 'name', errors: [] },
      { name: 'contactPeople', errors: [] },
      { name: 'taxId', errors: [] }
    ]);

    try {
      const response = await api.post('/suppliers', values);
      feedback.success(t('suppliers:messages.createSuccess', { name: values.name }));
      setModalVisible(false);
      // Jump straight into the new supplier's card so the operator can keep working.
      if (response.data?.id) {
        navigate(`/app/suppliers/${response.data.id}`);
      } else {
        fetchSuppliers();
      }
    } catch (error) {
      applySupplierSaveError(error, form, t, feedback, t('suppliers:messages.saveFailed', { name: values.name }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (supplier: Supplier) => {
    try {
      await api.delete(`/suppliers/${supplier.id}`);
      feedback.success(t('suppliers:supplierDeleted', { name: supplier.name }));
      fetchSuppliers();
    } catch (error: any) {
      const code = error.response?.data?.code;
      if (code === 'SUPPLIER_IN_USE') {
        feedback.error(t('suppliers:messages.deleteInUse', { name: supplier.name }), { sticky: true });
      } else {
        feedback.error(getApiErrorMessage(error, t, 'suppliers:messages.deleteError', undefined, { name: supplier.name }));
      }
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
  };

  // Filter and sort suppliers
  const filteredAndSortedSuppliers = useMemo(() => {
    let filtered = [...suppliers];

    // Apply search filter
    if (searchText) {
      filtered = filtered.filter((supplier) => matchesSupplierSearch(supplier, searchText));
    }

    // Apply sort
    filtered.sort((a, b) => {
      if (sortOrder === 'nameAsc') {
        return (a.name || '').localeCompare(b.name || '');
      } else {
        return (b.name || '').localeCompare(a.name || '');
      }
    });

    return filtered;
  }, [suppliers, searchText, sortOrder]);

  const renderSupplierIdentity = (supplier: Supplier) => (
    <div className="supplier-identity">
      <div className="supplier-identity__content">
        <Button
          type="link"
          className="supplier-identity__name-button"
          style={{ padding: 0, height: 'auto' }}
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/app/suppliers/${supplier.id}`);
          }}
        >
          <Text className="supplier-identity__name" title={supplier.name}>
            {supplier.name || EMPTY_VALUE}
          </Text>
        </Button>
        <div className="supplier-identity__chips">
          <Tag className="supplier-chip supplier-chip--tax">
            <IdcardOutlined />
            {supplier.taxId || t('suppliers:meta.noTaxId')}
          </Tag>
          <Tag className="supplier-chip supplier-chip--currency">
            <WalletOutlined />
            {supplier.defaultCurrency || 'PLN'}
          </Tag>
        </div>
      </div>
    </div>
  );

  const renderAddress = (address: string | null | undefined) => (
    <span className="supplier-address">
      <EnvironmentOutlined />
      <span title={address || undefined}>{address || EMPTY_VALUE}</span>
    </span>
  );

  const columns: TableColumnsType<Supplier> = [
    {
      title: t('suppliers:table.supplierInfo'),
      key: 'name',
      width: 360,
      render: (_value: unknown, record: Supplier) => renderSupplierIdentity(record),
    },
    {
      title: t('suppliers:table.contactPeople'),
      key: 'contactPeople',
      width: 280,
      render: (_value: unknown, record: Supplier) => (
        <SupplierContactCell
          contacts={record.contactPeople}
          contactPerson={record.contactPerson}
          phone={record.phone}
          email={record.email}
          emptyValue={EMPTY_VALUE}
        />
      ),
    },
    {
      title: t('suppliers:table.address'),
      dataIndex: 'address',
      key: 'address',
      width: 360,
      render: renderAddress,
    },
  ];

  const getRowMenuItems = (supplier: Supplier): MenuProps['items'] => {
    const items: NonNullable<MenuProps['items']> = [];

    if (canEditSuppliers) {
      items.push({
        key: 'edit',
        icon: <EditOutlined />,
        label: t('suppliers:menu.edit')
      });
    }

    if (canDeleteSuppliers) {
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: t('suppliers:menu.delete'),
        danger: true
      });
    }

    return items;
  };

  const confirmDelete = async (supplier: Supplier) => {
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('suppliers:confirmDeleteSupplier'),
        okText: t('common:yes'),
        cancelText: t('common:no'),
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) {
      return;
    }

    await handleDelete(supplier);
  };

  if (canEditSuppliers || canDeleteSuppliers) {
    columns.push({
      title: '',
      key: 'menu',
      width: 56,
      align: 'right',
      render: (_value: unknown, record: Supplier) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: getRowMenuItems(record),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();

              if (key === 'edit') {
                navigate(`/app/suppliers/${record.id}`);
              }

              if (key === 'delete') {
                void confirmDelete(record);
              }
            }
          }}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(event) => event.stopPropagation()}
            aria-label={t('suppliers:menu.open')}
          />
        </Dropdown>
      ),
    });
  }

  return (
    <div className="suppliers-page">
      <div className="suppliers-page__header">
        <h1>{t('suppliers:title')}</h1>
        {canEditSuppliers && (
          <Button
            className="suppliers-page__create-button"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            {t('suppliers:newSupplier')}
          </Button>
        )}
      </div>

      <div className="suppliers-page__toolbar">
        <Input
          className="suppliers-page__quick-search"
          placeholder={t('suppliers:supplierSearchPlaceholder')}
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
        <Select
          className="suppliers-page__sort"
          value={sortOrder}
          onChange={setSortOrder}
          suffixIcon={sortOrder === 'nameAsc' ? <SortAscendingOutlined /> : <SortDescendingOutlined />}
        >
          <Select.Option value="nameAsc">{t('suppliers:sortNameAsc')}</Select.Option>
          <Select.Option value="nameDesc">{t('suppliers:sortNameDesc')}</Select.Option>
        </Select>
        <span className="suppliers-page__count">
          {filteredAndSortedSuppliers.length}
        </span>
      </div>

      <Table
        className="suppliers-table"
        columns={columns}
        dataSource={filteredAndSortedSuppliers}
        rowKey="id"
        loading={loading}
        rowClassName="suppliers-table__row"
        scroll={{ x: 1060 }}
        locale={{
          emptyText: searchText ? t('suppliers:noSuppliersFound') : t('suppliers:noSuppliers'),
        }}
      />

      <Modal
        title={t('suppliers:createSupplier')}
        open={modalVisible}
        onCancel={handleModalClose}
        maskClosable={false}
        footer={null}
        width={600}
        destroyOnClose
      >
        <SupplierForm
          form={form}
          editingSupplier={null}
          submitting={submitting}
          submitLabel={`${t('suppliers:form.create')} ${t('suppliers:supplier')}`}
          onSubmit={handleCreate}
          open={modalVisible}
        />
      </Modal>
    </div>
  );
};

export default Suppliers;
